// hotel/F&B/FBCancelledOrdersReport.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Grow,
  Skeleton,
  Stack,
  FormHelperText,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Cancel,
  Search,
  Refresh,
  Receipt,
  TrendingUp,
  Schedule,
  Restore,
  Delete,
  Visibility,
  Print,
  Person,
  Phone,
  TableRestaurant,
  LocalHotel,
  AttachMoney,
  Fastfood,
  LocalBar,
  Category,
  Warning,
  CheckCircle,
  HourglassEmpty,
  History,
  Today,
  FileDownload,
  PictureAsPdf,
  BarChart,
  FilterList,
  Clear,
  TrendingDown,
  Assessment,
  CalendarToday,
  Storefront,
  RoomService,
  TakeoutDining,
  DeliveryDining,
  GetApp,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Frankville Hotels';

const ORDER_TYPES = [
  { value: 'dine_in', label: 'Bar/Skyline', icon: <LocalBar />, color: '#1976D2' },
  { value: 'Lounge', label: 'Lounge/Arena', icon: <LocalBar />, color: '#9C27B0' },
  { value: 'room_service', label: 'Room Service', icon: <RoomService />, color: '#2E7D32' },
  { value: 'takeaway', label: 'Takeaway', icon: <TakeoutDining />, color: '#ED6C02' },
  { value: 'delivery', label: 'Delivery', icon: <DeliveryDining />, color: '#0288D1' },
];

const CANCELLATION_REASONS = [
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'out_of_stock', label: 'Out of Stock' },
  { value: 'wrong_order', label: 'Wrong Order' },
  { value: 'delayed', label: 'Delayed Preparation' },
  { value: 'payment_issue', label: 'Payment Issue' },
  { value: 'table_changed', label: 'Table Changed' },
  { value: 'duplicate', label: 'Duplicate Order' },
  { value: 'guest_cancelled', label: 'Guest Left' },
  { value: 'other', label: 'Other' },
];

// ============================================
// STYLED COMPONENTS
// ============================================

const StatCard = styled(Card)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
  },
  ...(color && {
    '& .stat-icon': {
      backgroundColor: color,
      color: 'white',
    },
  }),
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
}));

// ============================================
// HELPER FUNCTIONS
// ============================================

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

const getCurrentStaffName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.first_name || storedProfile.name || 'Staff';
  } catch (error) {
    console.error('Error getting staff name:', error);
    return 'Staff';
  }
};

const formatCurrency = (amount) => {
  return `₦${parseFloat(amount || 0).toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  return dateTimeStr.split(' ')[0].split('T')[0];
};

// ============================================
// API SERVICE
// ============================================

const apiService = {
  getCancelledOrders: async (params = {}) => {
    try {
      const queryParams = {
        hotel_code: getHotelCode(),
        status: 'cancelled',
        ...params,
      };
      
      // Remove undefined or empty params
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined || queryParams[key] === '') {
          delete queryParams[key];
        }
      });

      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
        params: queryParams,
        timeout: 15000,
      });

      if (response.data && response.data.success) {
        return { success: true, orders: response.data.orders || [] };
      }
      return { success: false, orders: [], error: response.data?.error || 'Failed to fetch cancelled orders' };
    } catch (error) {
      console.error('Error fetching cancelled orders:', error);
      return { success: false, orders: [], error: error.message };
    }
  },

  getOrderStatistics: async (params = {}) => {
    try {
      const queryParams = {
        hotel_code: getHotelCode(),
        action: 'staff_statistics',
        ...params,
      };

      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
        params: queryParams,
        timeout: 10000,
      });

      if (response.data && response.data.success) {
        return { success: true, statistics: response.data.statistics || {} };
      }
      return { success: false, statistics: {} };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return { success: false, statistics: {} };
    }
  },

  getShiftHistory: async (params = {}) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
        params: {
          hotel_code: getHotelCode(),
          action: 'get_staff_shifts',
          ...params,
        },
        timeout: 10000,
      });

      if (response.data && response.data.success) {
        return { success: true, shifts: response.data.shifts || [] };
      }
      return { success: false, shifts: [] };
    } catch (error) {
      console.error('Error fetching shifts:', error);
      return { success: false, shifts: [] };
    }
  },
};

// ============================================
// MAIN COMPONENT
// ============================================

const FBCancelledOrdersReport = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  // Data states
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    total_cancelled: 0,
    total_refunded_amount: 0,
    total_original_value: 0,
    average_cancelled_value: 0,
    by_reason: [],
    by_type: [],
    by_waiter: [],
    daily_trend: [],
  });
  const [shifts, setShifts] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    orderType: '',
    cancellationReason: '',
    waiterId: '',
    minAmount: '',
    maxAmount: '',
    shiftId: '',
  });

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Report generation
  const [generatingReport, setGeneratingReport] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'chart'

  // ============================================
  // SNACKBAR
  // ============================================

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // ============================================
  // DATA LOADING FUNCTIONS
  // ============================================

  const loadCancelledOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.orderType) params.order_type = filters.orderType;
      if (filters.waiterId) params.created_by = filters.waiterId;
      if (filters.shiftId) params.shift_id = filters.shiftId;

      const result = await apiService.getCancelledOrders(params);
      
      if (result.success) {
        const cancelledOrders = result.orders.filter(o => o.status === 'cancelled');
        setOrders(cancelledOrders);
        setFilteredOrders(cancelledOrders);
        calculateStatistics(cancelledOrders);
      } else {
        showSnackbar(result.error || 'Failed to load cancelled orders', 'error');
      }
    } catch (error) {
      console.error('Error loading cancelled orders:', error);
      showSnackbar('An error occurred while loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadShifts = async () => {
    try {
      const result = await apiService.getShiftHistory();
      if (result.success) {
        setShifts(result.shifts || []);
      }
    } catch (error) {
      console.error('Error loading shifts:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const result = await apiService.getOrderStatistics({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });
      
      if (result.success && result.statistics) {
        // Merge with calculated statistics
        setStatistics(prev => ({
          ...prev,
          total_orders: result.statistics.total_orders || 0,
          total_revenue: result.statistics.total_revenue || 0,
        }));
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  // ============================================
  // STATISTICS CALCULATION
  // ============================================

  const calculateStatistics = (orderData) => {
    const cancelled = orderData || orders;
    
    // Total cancelled orders
    const totalCancelled = cancelled.length;
    
    // Total refunded amount (sum of final_amount for cancelled orders)
    const totalRefunded = cancelled.reduce((sum, order) => sum + parseFloat(order.final_amount || 0), 0);
    
    // Total original value (sum of total_amount before cancellation)
    const totalOriginal = cancelled.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
    
    // Average cancelled order value
    const avgValue = totalCancelled > 0 ? totalRefunded / totalCancelled : 0;
    
    // Breakdown by cancellation reason
    const reasonMap = {};
    cancelled.forEach(order => {
      const reason = order.cancellation_reason || 'Not specified';
      if (!reasonMap[reason]) {
        reasonMap[reason] = { count: 0, total_amount: 0, orders: [] };
      }
      reasonMap[reason].count++;
      reasonMap[reason].total_amount += parseFloat(order.final_amount || 0);
      reasonMap[reason].orders.push(order);
    });
    
    const byReason = Object.entries(reasonMap).map(([reason, data]) => ({
      reason,
      count: data.count,
      total_amount: data.total_amount,
      percentage: totalCancelled > 0 ? (data.count / totalCancelled) * 100 : 0,
    })).sort((a, b) => b.count - a.count);
    
    // Breakdown by order type
    const typeMap = {};
    cancelled.forEach(order => {
      const type = order.order_type || 'unknown';
      if (!typeMap[type]) {
        typeMap[type] = { count: 0, total_amount: 0 };
      }
      typeMap[type].count++;
      typeMap[type].total_amount += parseFloat(order.final_amount || 0);
    });
    
    const byType = Object.entries(typeMap).map(([type, data]) => ({
      type,
      label: ORDER_TYPES.find(t => t.value === type)?.label || type,
      count: data.count,
      total_amount: data.total_amount,
      percentage: totalCancelled > 0 ? (data.count / totalCancelled) * 100 : 0,
    })).sort((a, b) => b.count - a.count);
    
    // Breakdown by waiter
    const waiterMap = {};
    cancelled.forEach(order => {
      const waiter = order.created_by_name || order.created_by || 'Unknown';
      if (!waiterMap[waiter]) {
        waiterMap[waiter] = { count: 0, total_amount: 0 };
      }
      waiterMap[waiter].count++;
      waiterMap[waiter].total_amount += parseFloat(order.final_amount || 0);
    });
    
    const byWaiter = Object.entries(waiterMap).map(([waiter, data]) => ({
      waiter,
      count: data.count,
      total_amount: data.total_amount,
      percentage: totalCancelled > 0 ? (data.count / totalCancelled) * 100 : 0,
    })).sort((a, b) => b.count - a.count);
    
    // Daily trend
    const dailyMap = {};
    cancelled.forEach(order => {
      const date = getDateOnly(order.created_at);
      if (!dailyMap[date]) {
        dailyMap[date] = { count: 0, total_amount: 0 };
      }
      dailyMap[date].count++;
      dailyMap[date].total_amount += parseFloat(order.final_amount || 0);
    });
    
    const dailyTrend = Object.entries(dailyMap).map(([date, data]) => ({
      date,
      count: data.count,
      total_amount: data.total_amount,
    })).sort((a, b) => a.date.localeCompare(b.date));
    
    setStatistics({
      total_cancelled: totalCancelled,
      total_refunded_amount: totalRefunded,
      total_original_value: totalOriginal,
      average_cancelled_value: avgValue,
      by_reason: byReason,
      by_type: byType,
      by_waiter: byWaiter,
      daily_trend: dailyTrend,
    });
  };

  // ============================================
  // FILTERING FUNCTIONS
  // ============================================

  const applyFilters = useCallback(() => {
    let filtered = [...orders];

    // Date range filter
    if (filters.startDate) {
      filtered = filtered.filter(order => 
        getDateOnly(order.created_at) >= filters.startDate
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(order => 
        getDateOnly(order.created_at) <= filters.endDate
      );
    }

    // Order type filter
    if (filters.orderType) {
      filtered = filtered.filter(order => 
        order.order_type === filters.orderType
      );
    }

    // Cancellation reason filter
    if (filters.cancellationReason) {
      filtered = filtered.filter(order => 
        (order.cancellation_reason || '').toLowerCase().includes(filters.cancellationReason.toLowerCase())
      );
    }

    // Waiter filter
    if (filters.waiterId) {
      filtered = filtered.filter(order => 
        order.created_by === filters.waiterId || 
        order.created_by_name === filters.waiterId
      );
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(order => 
        parseFloat(order.final_amount || 0) >= parseFloat(filters.minAmount)
      );
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(order => 
        parseFloat(order.final_amount || 0) <= parseFloat(filters.maxAmount)
      );
    }

    // Shift filter
    if (filters.shiftId) {
      filtered = filtered.filter(order => 
        order.shift_id === parseInt(filters.shiftId)
      );
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(query) ||
        order.guest_name?.toLowerCase().includes(query) ||
        order.table_number?.toLowerCase().includes(query) ||
        order.room_number?.toLowerCase().includes(query) ||
        (order.cancellation_reason || '').toLowerCase().includes(query)
      );
    }

    setFilteredOrders(filtered);
    setPage(0);
    calculateStatistics(filtered);
  }, [orders, filters, searchQuery]);

  // ============================================
  // HANDLER FUNCTIONS
  // ============================================

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleClearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0];
    setFilters({
      startDate: weekAgo,
      endDate: today,
      orderType: '',
      cancellationReason: '',
      waiterId: '',
      minAmount: '',
      maxAmount: '',
      shiftId: '',
    });
    setSearchQuery('');
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================
  // REPORT GENERATION
  // ============================================

  const generateReport = (format = 'print') => {
    setGeneratingReport(true);
    
    try {
      const totalCancelled = filteredOrders.length;
      const totalRefunded = filteredOrders.reduce((sum, order) => sum + parseFloat(order.final_amount || 0), 0);
      const totalOriginal = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
      const avgValue = totalCancelled > 0 ? totalRefunded / totalCancelled : 0;

      // Build report HTML
      const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Cancelled Orders Report</title>
          <style>
            @media print {
              body { margin: 20px; }
              .no-print { display: none; }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 1200px;
              margin: 0 auto;
            }
            .report-header {
              text-align: center;
              border-bottom: 3px solid #d32f2f;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .report-header h1 {
              color: #d32f2f;
              margin: 0;
            }
            .report-header .subtitle {
              color: #666;
              margin: 5px 0;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-box {
              background: #f5f5f5;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
            }
            .stat-box .label {
              font-size: 12px;
              color: #666;
              margin: 0;
            }
            .stat-box .value {
              font-size: 20px;
              font-weight: bold;
              margin: 5px 0 0;
            }
            .stat-box .value.negative { color: #d32f2f; }
            .stat-box .value.positive { color: #4caf50; }
            .table-container {
              overflow-x: auto;
              margin-top: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th {
              background: #d32f2f;
              color: white;
              padding: 10px;
              text-align: left;
              border: 1px solid #ddd;
            }
            td {
              padding: 8px 10px;
              border: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              color: #999;
              text-align: center;
            }
            .summary-section {
              margin: 20px 0;
              padding: 15px;
              background: #fafafa;
              border-radius: 8px;
            }
            .summary-section h3 {
              margin-top: 0;
              color: #333;
            }
            .reason-breakdown {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 10px;
              margin-top: 10px;
            }
            .reason-item {
              background: white;
              padding: 10px;
              border-radius: 4px;
              border-left: 3px solid #d32f2f;
            }
            .reason-item .reason-name {
              font-weight: bold;
              font-size: 13px;
            }
            .reason-item .reason-count {
              color: #666;
              font-size: 12px;
            }
            .badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: bold;
            }
            .badge-warning { background: #ff9800; color: white; }
            .badge-danger { background: #d32f2f; color: white; }
            .badge-info { background: #2196f3; color: white; }
          </style>
        </head>
        <body>
          <div class="report-header">
            <h1>${HOTEL_NAME}</h1>
            <p class="subtitle">Food & Beverage - Cancelled Orders Report</p>
            <p class="subtitle">
              Period: ${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}
              ${filters.orderType ? ` | Type: ${ORDER_TYPES.find(t => t.value === filters.orderType)?.label || filters.orderType}` : ''}
              ${filters.cancellationReason ? ` | Reason: ${filters.cancellationReason}` : ''}
            </p>
            <p class="subtitle">Generated: ${new Date().toLocaleString()}</p>
          </div>

          <div class="stats-grid">
            <div class="stat-box">
              <p class="label">Total Cancelled Orders</p>
              <p class="value negative">${totalCancelled}</p>
            </div>
            <div class="stat-box">
              <p class="label">Total Refunded Amount</p>
              <p class="value negative">${formatCurrency(totalRefunded)}</p>
            </div>
            <div class="stat-box">
              <p class="label">Average Cancelled Value</p>
              <p class="value">${formatCurrency(avgValue)}</p>
            </div>
            <div class="stat-box">
              <p class="label">Total Original Value</p>
              <p class="value">${formatCurrency(totalOriginal)}</p>
            </div>
          </div>

          ${statistics.by_reason && statistics.by_reason.length > 0 ? `
            <div class="summary-section">
              <h3>📊 Cancellation Breakdown by Reason</h3>
              <div class="reason-breakdown">
                ${statistics.by_reason.slice(0, 8).map(item => `
                  <div class="reason-item">
                    <div class="reason-name">${item.reason}</div>
                    <div class="reason-count">${item.count} orders (${item.percentage.toFixed(1)}%)</div>
                    <div style="font-size:11px;color:#666;">${formatCurrency(item.total_amount)}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <div class="table-container">
            <h3>📋 Cancelled Orders Details</h3>
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Table/Room</th>
                  <th>Guest</th>
                  <th>Items</th>
                  <th>Original Amount</th>
                  <th>Cancelled Amount</th>
                  <th>Reason</th>
                  <th>Waiter</th>
                </tr>
              </thead>
              <tbody>
                ${filteredOrders.map(order => `
                  <tr>
                    <td><strong>${order.order_number}</strong></td>
                    <td>${formatDateTime(order.created_at)}</td>
                    <td>${ORDER_TYPES.find(t => t.value === order.order_type)?.label || order.order_type}</td>
                    <td>${order.table_number || order.room_number || '-'}</td>
                    <td>${order.guest_name}</td>
                    <td>${order.items?.length || 0}</td>
                    <td>${formatCurrency(order.total_amount)}</td>
                    <td style="color:#d32f2f;">${formatCurrency(order.final_amount)}</td>
                    <td>${order.cancellation_reason || 'Not specified'}</td>
                    <td>${order.created_by_name || order.created_by || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="footer">
            <p>Report generated by: ${getCurrentStaffName()}</p>
            <p>This is a computer-generated report. No signature required.</p>
            <p>${HOTEL_NAME} F&B System</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 1000);
            };
          <\/script>
        </body>
        </html>
      `;

      if (format === 'print') {
        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (printWindow) {
          printWindow.document.write(reportHTML);
          printWindow.document.close();
          showSnackbar('Report generated successfully!', 'success');
        } else {
          showSnackbar('Please allow popups for this site', 'error');
        }
      } else if (format === 'csv') {
        downloadCSV();
      }
    } catch (error) {
      console.error('Error generating report:', error);
      showSnackbar('Failed to generate report', 'error');
    } finally {
      setGeneratingReport(false);
    }
  };

  const downloadCSV = () => {
    const headers = ['Order #', 'Date', 'Type', 'Table/Room', 'Guest Name', 'Items Count', 'Original Amount', 'Cancelled Amount', 'Reason', 'Waiter', 'Cancelled At'];
    
    const rows = filteredOrders.map(order => [
      order.order_number,
      formatDateTime(order.created_at),
      ORDER_TYPES.find(t => t.value === order.order_type)?.label || order.order_type,
      order.table_number || order.room_number || '-',
      order.guest_name,
      order.items?.length || 0,
      parseFloat(order.total_amount || 0).toFixed(2),
      parseFloat(order.final_amount || 0).toFixed(2),
      order.cancellation_reason || 'Not specified',
      order.created_by_name || order.created_by || '-',
      order.cancelled_at ? formatDateTime(order.cancelled_at) : '-',
    ]);

    // Add summary row
    const totalCancelled = filteredOrders.length;
    const totalRefunded = filteredOrders.reduce((sum, order) => sum + parseFloat(order.final_amount || 0), 0);
    rows.push(['', '', '', '', '', 'TOTAL', '', totalRefunded.toFixed(2), '', '', '']);
    rows.push(['', '', '', '', '', 'COUNT', totalCancelled, '', '', '', '']);

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Cancelled_Orders_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showSnackbar('CSV exported successfully!', 'success');
  };

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    loadCancelledOrders();
    loadShifts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [orders, filters, searchQuery]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const getStatusColor = (reason) => {
    if (!reason) return '#9e9e9e';
    const reasonLower = reason.toLowerCase();
    if (reasonLower.includes('stock') || reasonLower.includes('out of stock')) return '#f44336';
    if (reasonLower.includes('customer') || reasonLower.includes('request')) return '#ff9800';
    if (reasonLower.includes('delayed') || reasonLower.includes('time')) return '#2196f3';
    if (reasonLower.includes('payment') || reasonLower.includes('issue')) return '#9c27b0';
    if (reasonLower.includes('wrong') || reasonLower.includes('error')) return '#e91e63';
    return '#757575';
  };

  const getTypeIcon = (typeValue) => {
    const type = ORDER_TYPES.find(t => t.value === typeValue);
    return type ? type.icon : <LocalBar />;
  };

  const getTypeColor = (typeValue) => {
    const type = ORDER_TYPES.find(t => t.value === typeValue);
    return type ? type.color : '#757575';
  };

  // ============================================
  // RENDER: STATISTICS TAB
  // ============================================

  const renderStatisticsTab = () => {
    const { total_cancelled, total_refunded_amount, average_cancelled_value, by_reason, by_type, by_waiter, daily_trend } = statistics;

    const statCards = [
      { 
        title: 'Total Cancelled', 
        value: total_cancelled, 
        icon: <Cancel />, 
        color: '#d32f2f',
        subtitle: 'Orders cancelled',
      },
      { 
        title: 'Total Refunded Amount', 
        value: formatCurrency(total_refunded_amount), 
        icon: <AttachMoney />, 
        color: '#f44336',
        subtitle: 'Total value of cancelled orders',
      },
      { 
        title: 'Average Cancelled Value', 
        value: formatCurrency(average_cancelled_value), 
        icon: <TrendingDown />, 
        color: '#ff9800',
        subtitle: 'Per cancelled order',
      },
      { 
        title: 'Unique Waiters', 
        value: by_waiter?.length || 0, 
        icon: <Person />, 
        color: '#2196f3',
        subtitle: 'Staff with cancellations',
      },
    ];

    return (
      <Box>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Zoom in timeout={index * 100}>
                <StatCard color={stat.color}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {stat.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {stat.subtitle}
                        </Typography>
                      </Box>
                      <Avatar className="stat-icon" sx={{ bgcolor: stat.color, width: 50, height: 50 }}>
                        {stat.icon}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StatCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Cancellation by Reason */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning /> Cancellation by Reason
              </Typography>
              {by_reason && by_reason.length > 0 ? (
                <List>
                  {by_reason.slice(0, 6).map((item, index) => (
                    <ListItem key={index} divider={index < by_reason.length - 1}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: getStatusColor(item.reason), width: 30, height: 30 }}>
                          <Cancel sx={{ fontSize: 16, color: 'white' }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.reason}
                        secondary={`${item.count} orders (${item.percentage.toFixed(1)}%) • ${formatCurrency(item.total_amount)}`}
                      />
                      <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>{item.count}</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={item.percentage}
                          sx={{ width: 60, borderRadius: 2, mt: 0.5 }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No cancellation data available
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Cancellation by Order Type */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Category /> Cancellation by Order Type
              </Typography>
              {by_type && by_type.length > 0 ? (
                <List>
                  {by_type.map((item, index) => (
                    <ListItem key={index} divider={index < by_type.length - 1}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: getTypeColor(item.type), width: 30, height: 30 }}>
                          {getTypeIcon(item.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        secondary={`${item.count} orders (${item.percentage.toFixed(1)}%) • ${formatCurrency(item.total_amount)}`}
                      />
                      <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>{item.count}</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={item.percentage}
                          sx={{ width: 60, borderRadius: 2, mt: 0.5 }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No cancellation data available
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Daily Trend */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp /> Daily Cancellation Trend
              </Typography>
              {daily_trend && daily_trend.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ display: 'flex', gap: 2, minWidth: 'max-content', py: 2 }}>
                    {daily_trend.map((day, index) => {
                      const maxCount = Math.max(...daily_trend.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 150;
                      return (
                        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                          <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
                            {formatCurrency(day.total_amount)}
                          </Typography>
                          <Box sx={{ 
                            height: 180, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}>
                            <Box sx={{ 
                              width: 40, 
                              height: Math.max(height, 10),
                              bgcolor: day.count > 5 ? '#d32f2f' : day.count > 2 ? '#ff9800' : '#2196f3',
                              borderRadius: '4px 4px 0 0',
                              transition: 'height 0.5s ease',
                              position: 'relative',
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}>
                              <Typography variant="caption" sx={{ 
                                position: 'absolute', 
                                top: -18, 
                                left: '50%', 
                                transform: 'translateX(-50%)',
                                fontWeight: 'bold',
                              }}>
                                {day.count}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                            {formatDate(day.date)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No daily trend data available
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ============================================
  // RENDER: ORDERS TABLE TAB
  // ============================================

  const renderOrdersTable = () => {
    const displayedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box>
        {/* Filter Bar */}
        <FilterPaper>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by order #, guest, table, reason..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={filters.startDate}
                onChange={handleDateChange('startDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={filters.endDate}
                onChange={handleDateChange('endDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={filters.orderType}
                  onChange={handleFilterChange('orderType')}
                  label="Order Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {ORDER_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth sx={{ minWidth: 150 }}>
                <InputLabel>Cancellation Reason</InputLabel>
                <Select
                  value={filters.cancellationReason}
                  onChange={handleFilterChange('cancellationReason')}
                  label="Cancellation Reason"
                >
                  <MenuItem value="">All Reasons</MenuItem>
                  {CANCELLATION_REASONS.map((reason) => (
                    <MenuItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              <Chip
                label={`${filteredOrders.length} orders found`}
                size="small"
                color="primary"
                variant="outlined"
              />
              {filters.startDate && filters.endDate && (
                <Chip
                  label={`${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`}
                  size="small"
                  icon={<CalendarToday />}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<Print />}
                onClick={() => generateReport('print')}
                disabled={filteredOrders.length === 0}
              >
                Print Report
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<GetApp />}
                onClick={() => generateReport('csv')}
                disabled={filteredOrders.length === 0}
              >
                Export CSV
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={loadCancelledOrders}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </FilterPaper>

        {/* Orders Table */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#d32f2f' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Order #</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Table/Room</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Guest</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Items</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Original</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cancelled</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reason</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Waiter</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          <Cancel sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body1" color="textSecondary">
                            No cancelled orders found
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Try adjusting your filters or date range
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedOrders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {order.order_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(order.created_at)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {new Date(order.created_at).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getTypeIcon(order.order_type)}
                              label={ORDER_TYPES.find(t => t.value === order.order_type)?.label || order.order_type}
                              size="small"
                              sx={{ backgroundColor: getTypeColor(order.order_type), color: 'white' }}
                            />
                          </TableCell>
                          <TableCell>
                            {order.table_number || order.room_number || '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{order.guest_name}</Typography>
                            {order.guest_phone && (
                              <Typography variant="caption" color="textSecondary">
                                {order.guest_phone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {order.items?.length || 0}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(order.total_amount)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="error">
                              {formatCurrency(order.final_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.cancellation_reason || 'Not specified'}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(order.cancellation_reason),
                                color: 'white',
                                maxWidth: 150,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {order.created_by_name || order.created_by || '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton size="small" color="primary" onClick={() => handleViewDetails(order)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredOrders.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredOrders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
            </>
          )}
        </Paper>
      </Box>
    );
  };

  // ============================================
  // RENDER: DETAILS DIALOG
  // ============================================

  const renderDetailsDialog = () => (
    <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel />
          <Typography variant="h6">Cancelled Order Details</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {selectedOrder && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Order Information</Typography>
                <Typography variant="body2"><strong>Order #:</strong> {selectedOrder.order_number}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {formatDateTime(selectedOrder.created_at)}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {ORDER_TYPES.find(t => t.value === selectedOrder.order_type)?.label || selectedOrder.order_type}</Typography>
                {selectedOrder.table_number && (
                  <Typography variant="body2"><strong>Table:</strong> {selectedOrder.table_number}</Typography>
                )}
                {selectedOrder.room_number && (
                  <Typography variant="body2"><strong>Room:</strong> {selectedOrder.room_number}</Typography>
                )}
                <Typography variant="body2"><strong>Guest:</strong> {selectedOrder.guest_name}</Typography>
                {selectedOrder.guest_phone && (
                  <Typography variant="body2"><strong>Phone:</strong> {selectedOrder.guest_phone}</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Cancellation Details</Typography>
                <Typography variant="body2"><strong>Status:</strong> <Chip label="CANCELLED" size="small" color="error" /></Typography>
                <Typography variant="body2"><strong>Reason:</strong> {selectedOrder.cancellation_reason || 'Not specified'}</Typography>
                {selectedOrder.cancelled_by && (
                  <Typography variant="body2"><strong>Cancelled By:</strong> {selectedOrder.cancelled_by}</Typography>
                )}
                {selectedOrder.cancelled_at && (
                  <Typography variant="body2"><strong>Cancelled At:</strong> {formatDateTime(selectedOrder.cancelled_at)}</Typography>
                )}
                <Typography variant="body2"><strong>Waiter:</strong> {selectedOrder.created_by_name || selectedOrder.created_by || '-'}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Order Items</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedOrder.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.item_name}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right"><strong>Original Total:</strong></TableCell>
                    <TableCell align="right"><strong>{formatCurrency(selectedOrder.total_amount)}</strong></TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#FFEBEE' }}>
                    <TableCell colSpan={3} align="right"><strong style={{ color: '#d32f2f' }}>Cancelled Amount:</strong></TableCell>
                    <TableCell align="right"><strong style={{ color: '#d32f2f' }}>{formatCurrency(selectedOrder.final_amount)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {selectedOrder.special_instructions && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF3E0', borderRadius: 2 }}>
                <Typography variant="subtitle2">Special Instructions:</Typography>
                <Typography variant="body2">{selectedOrder.special_instructions}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" startIcon={<Print />} onClick={() => {
          const order = selectedOrder;
          handleCloseDetailsDialog();
          setTimeout(() => {
            const printWindow = window.open('', '_blank', 'width=400,height=600');
            if (printWindow) {
              const content = `
                <!DOCTYPE html>
                <html>
                <head><title>Cancelled Order - ${order.order_number}</title>
                <style>
                  body { font-family: monospace; padding: 20px; max-width: 400px; margin: 0 auto; }
                  .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; }
                  .divider { border-top: 1px dashed #000; margin: 10px 0; }
                  .row { display: flex; justify-content: space-between; margin: 5px 0; }
                  .total { font-size: 16px; font-weight: bold; }
                  .cancelled { color: #d32f2f; font-weight: bold; }
                  .footer { text-align: center; margin-top: 20px; font-size: 12px; }
                </style>
                </head>
                <body>
                  <div class="header">
                    <h2>CANCELLED ORDER</h2>
                    <h3>#${order.order_number}</h3>
                  </div>
                  <div class="row"><span>Date:</span><span>${formatDateTime(order.created_at)}</span></div>
                  <div class="row"><span>Type:</span><span>${ORDER_TYPES.find(t => t.value === order.order_type)?.label || order.order_type}</span></div>
                  ${order.table_number ? `<div class="row"><span>Table:</span><span>${order.table_number}</span></div>` : ''}
                  ${order.room_number ? `<div class="row"><span>Room:</span><span>${order.room_number}</span></div>` : ''}
                  <div class="row"><span>Guest:</span><span>${order.guest_name}</span></div>
                  <div class="divider"></div>
                  ${order.items?.map(item => `
                    <div class="row"><span>${item.quantity}x ${item.item_name}</span><span>${formatCurrency(item.quantity * item.unit_price)}</span></div>
                  `).join('')}
                  <div class="divider"></div>
                  <div class="row"><span>Original Total:</span><span>${formatCurrency(order.total_amount)}</span></div>
                  <div class="row cancelled"><span>CANCELLED AMOUNT:</span><span>${formatCurrency(order.final_amount)}</span></div>
                  <div class="divider"></div>
                  <div class="row"><span>Cancellation Reason:</span></div>
                  <div style="text-align:center;">${order.cancellation_reason || 'Not specified'}</div>
                  <div class="footer">This order has been CANCELLED and is invalid for payment.</div>
                </body>
                </html>
              `;
              printWindow.document.write(content);
              printWindow.document.close();
              setTimeout(() => { printWindow.print(); }, 500);
            }
          }, 300);
        }}>
          Print Details
        </Button>
        <Button onClick={handleCloseDetailsDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  const tabLabels = [
    { label: '📋 Orders', icon: <Receipt /> },
    { label: '📊 Analytics', icon: <BarChart /> },
  ];

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
          transition: 'margin 0.3s ease',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{
          p: 4,
          mt: 3,
          background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Cancel sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Cancelled Orders Report
                </Typography>
              
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${filteredOrders.length} cancelled orders`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              {filters.startDate && filters.endDate && (
                <Chip
                  label={`${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  icon={<CalendarToday sx={{ color: 'white' }} />}
                />
              )}
              <Chip
                label={`Total Refunded: ${formatCurrency(statistics.total_refunded_amount || 0)}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                icon={<AttachMoney sx={{ color: 'white' }} />}
              />
            </Box>
          </Box>
          <Box sx={{
            position: 'absolute',
            right: -30,
            bottom: -30,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}>
            <Cancel sx={{ fontSize: 200 }} />
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper elevation={1} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                minWidth: 120,
              },
              '& .Mui-selected': {
                color: '#d32f2f',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#d32f2f',
                height: 3,
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 ? renderOrdersTable() : renderStatisticsTab()}
          </Box>
        </Paper>
      </Box>

      {/* Details Dialog */}
      {renderDetailsDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FBCancelledOrdersReport;